import Alpine from 'alpinejs'
import { pushDataLayer } from '../helpers/pushDataLayer'
import { leaseTypes } from '../leaseTypes'
import { Endpoints } from '../site'
type Props = {
  lease_type: string
  city: string
}

type metaProps = {
  body: string | null
  heading: string | null
  meta_description: string | null
  page_title: string | null
}

type fetchLeasesProps = {
  lease_type?: string
  cities?: string[]
  query?: string
  size_from?: number | null
  size_to?: number | null
  price_from?: number | null
  price_to?: number | null
  price_pa_from?: number | null
  price_pa_to?: number | null
  price_type?: string
}

// Used for the mobile view (drawer)
Alpine.store('LeasesList', {
  isOpen: false,

  open() {
    this.isOpen = true
    Alpine.store('Backdrop').show()
    window.scrollTo(0, 0)
  },

  close() {
    this.isOpen = false
    Alpine.store('Backdrop').hide()
    window.scrollTo(0, 0)
  },

  toggle() {
    this.isOpen = !this.isOpen
    Alpine.store('Backdrop').toggle()
    window.scrollTo(0, 0)
  },
})

// The initial form data
const initialFormData = () => ({
  lease_type: '',
  size_from: 0,
  size_to: 6000,
  price_from: 0,
  price_to: 100000,
  price_pa_from: 0,
  price_pa_to: 6000,
  price_type: 'monthly',
  query: '',
  cities: [],
})

document.addEventListener('alpine:init', () => {
  Alpine.data('leaseSearch', ({ lease_type, city }: Props) => ({
    typeFilterDisabled: false,
    mapExpanded: false,
    isLoading: true,
    isDirty: false,
    leases: [],
    altLeases: [],
    meta: {
      body: null,
      heading: null,
      meta_description: null,
      page_title: null,
    },
    // The data from the filters goes into formData.
    formData: initialFormData(),
    defaultFormData: initialFormData(),
    cities: [],
    lease_types: leaseTypes,

    initialFormData() {
      return initialFormData()
    },

    scrollToTop() {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    },

    typeFilterLabel() {
      if (this.formData.lease_type) {
        return this.lease_types.find((type) => type.id === this.formData.lease_type)?.label
      }
      return 'Ejendomstype'
    },

    sizeFilterLabel() {
      if (
        this.formData.size_from !== initialFormData().size_from ||
        this.formData.size_to !== initialFormData().size_to
      ) {
        return `${this.formData.size_from} m<sup>2</sup> - ${this.formData.size_to}${
          this.formData.size_to === initialFormData().size_to ? '+' : ''
        } m<sup>2</sup>`
      } else {
        return 'Størrelse'
      }
    },

    setPriceFilterLabel(
      priceFrom: number,
      priceTo: number,
      initPriceFrom: number,
      initPriceTo: number
    ): string {
      if (priceFrom !== initPriceFrom || priceTo !== initPriceTo) {
        return `${priceFrom} kr. - ${priceTo}${priceTo === initPriceTo ? '+' : ''} kr.`
      }
      return 'Pris'
    },

    priceFilterLabel() {
      if (this.formData.price_type === 'monthly') {
        return this.setPriceFilterLabel(
          this.formData.price_from,
          this.formData.price_to,
          initialFormData().price_from,
          initialFormData().price_to
        )
      }
      if (this.formData.price_type !== 'monthly') {
        return this.setPriceFilterLabel(
          this.formData.price_pa_from,
          this.formData.price_pa_to,
          initialFormData().price_pa_from,
          initialFormData().price_pa_to
        )
      }
    },

    handleDirty() {
      JSON.stringify(this.formData) !== JSON.stringify(initialFormData())
        ? (this.isDirty = true)
        : (this.isDirty = false)
    },

    // Reset filters to initial state.
    resetFilters() {
      this.formData = initialFormData()
    },

    // Fetch all cities from the API.
    fetchCitites(leaseType?: string) {
      const leaseTypeString = leaseType ? `&lease_type=${leaseType}` : ''
      return fetch(
        `${import.meta.env.MIX_API_BASE as string}${Endpoints.cities}?sortby=name&direction=asc${leaseTypeString}`
      )
        .then((response) => response.json())
        .then((data) => {
          return (this.cities = data.data)
        })
    },

    async fetchAltLeases(url: URL) {
      const altLeasesUrl = `${url}&alternative_leases=true`

      this.isLoading = true

      // Call API with the generated url.
      await fetch(altLeasesUrl)
        .then((response) => {
          if (response.ok) {
            return response.json()
          }
          throw new Error('Der skete en fejl med at hente alternative lejemål')
        })
        .then((data) => {
          this.altLeases = data.data
          this.isLoading = false
          pushDataLayer({
            event: 'filter',
            filter_result:
              this.altLeases.length === 0 ? 'no properties' : 'no properties with alternatives',
          })
        })
        .catch(() => {
          window.dispatchEvent(
            new CustomEvent('notice', {
              detail: {
                type: 'error',
                text: 'Der skete en fejl med at hente alternative lejemål',
              },
            })
          )
          this.isLoading = false
        })
    },

    async fetchLeases({
      lease_type = '',
      cities = [],
      query = '',
      size_from = null,
      size_to = null,
      price_from = null,
      price_to = null,
      price_pa_from = null,
      price_pa_to = null,
      price_type = 'monthly',
    }: fetchLeasesProps) {
      const citiyObjects = JSON.parse(JSON.stringify(this.cities))
      const filteredCities = citiyObjects.filter((city: any) => cities.includes(city.slug))
      const filteredCitiesIds = filteredCities.map((city: any) => city.id)

      const apiBase = `${import.meta.env.MIX_API_BASE as string}${Endpoints.leasesSearch}`
      const url = new URL(apiBase)

      // Add search query params
      lease_type && url.searchParams.set('lease_type', lease_type)
      query && url.searchParams.set('query', query)
      size_from && url.searchParams.set('size_from', size_from.toString())

      if (price_type === initialFormData().price_type) {
        price_from && url.searchParams.set('price_from', price_from.toString())
        price_to &&
          price_to !== initialFormData().price_to &&
          url.searchParams.set('price_to', price_to.toString())
      } else {
        price_pa_from && url.searchParams.set('price_pa_from', price_pa_from.toString())
        price_pa_to &&
          price_pa_to !== initialFormData().price_pa_to &&
          url.searchParams.set('price_pa_to', price_pa_to.toString())
      }

      // If size_to is at it's max, don't add it to the query.
      size_to &&
        size_to !== initialFormData().size_to &&
        url.searchParams.set('size_to', size_to.toString())

      filteredCitiesIds.length > 0 && url.searchParams.set('cities', filteredCitiesIds?.join(','))

      this.isLoading = true

      // Call API with the generated url.
      await fetch(url)
        .then((response) => {
          if (response.ok) {
            return response.json()
          }
          throw new Error('Der skete en fejl med at hente lejemål')
        })
        .then((data) => {
          this.leases = data.data
          this.altLeases = []
          this.meta = data.meta
          this.isLoading = false

          document.title =
            this.meta.page_title ?? 'Udlejning af erhvervsejendomme | Udlejning af boliger'
        })
        .catch(() => {
          window.dispatchEvent(
            new CustomEvent('notice', {
              detail: { type: 'error', text: 'Der skete en fejl med at hente lejemål' },
            })
          )
          this.isLoading = false
        })

      if (this.leases.length === 0) {
        await this.fetchAltLeases(url)
      }
    },

    // Handle the cities in the url.
    updateUrlArea(cities: string[]) {
      // primaryCity is a part of the url.
      // additionalCities city is a url parameter (by).
      var additionalCities = cities.slice()
      var primaryCity = additionalCities.shift()

      const url = primaryCity
        ? new URL(
            `${window.location.origin}/udlejning/${
              window.location.pathname.split('/')[2]
            }/${primaryCity}${window.location.search}`
          )
        : new URL(
            `${window.location.origin}/udlejning/${window.location.pathname.split('/')[2]}${
              window.location.search
            }`
          )

      if (additionalCities.length === 0) {
        url.searchParams.delete('by')
      } else {
        if (window.location.search.includes('by')) {
          url.searchParams.set('by', additionalCities.join(','))
        } else {
          url.searchParams.append('by', additionalCities.join(','))
        }
      }

      history.pushState(null, '', url.toString())
    },

    // Handle the sliders in the url.
    updateUrlSliderParams(value: number, field: string) {
      const url = new URL(`${window.location.href}`)

      if (value === initialFormData()[field]) {
        url.searchParams.delete(field)
      } else {
        if (window.location.search.includes(field)) {
          url.searchParams.set(field, value.toString())
        } else {
          url.searchParams.append(field, value.toString())
        }
      }

      history.pushState(null, '', url.toString())
    },

    // Handle the price type in the url.
    updateUrlPriceTypeParams(
      type: string,
      priceFrom: number,
      priceTo: number,
      paFrom: number,
      paTo: number
    ) {
      const url = new URL(`${window.location.href}`)

      if (type === initialFormData().price_type) {
        url.searchParams.delete('price_pa_from')
        url.searchParams.delete('price_pa_to')
        priceFrom !== initialFormData().price_from &&
          url.searchParams.append('price_from', priceFrom.toString())
        priceTo !== initialFormData().price_to &&
          url.searchParams.append('price_to', priceTo.toString())
      } else {
        url.searchParams.delete('price_from')
        url.searchParams.delete('price_to')
        paFrom !== initialFormData().price_pa_from &&
          url.searchParams.append('price_pa_from', paFrom.toString())
        paTo !== initialFormData().price_pa_to &&
          url.searchParams.append('price_pa_to', paTo.toString())
      }

      history.pushState(null, '', url.toString())
    },

    // Handle the Type in the url.
    updateUrlType(lease_type: string, cities: string[]) {
      let url = new URL(`${window.location.origin}/udlejning`)

      if (cities.length >= 1) {
        url = new URL(
          `${window.location.origin}/udlejning/${lease_type}/${
            window.location.pathname.split('/')[3]
          }${window.location.search}`
        )
      } else {
        url = new URL(`${window.location.origin}/udlejning/${lease_type}${window.location.search}`)
      }

      history.pushState(null, '', url.toString())
    },

    async init() {
      const url = new URL(window.location.href)
      let params = new URLSearchParams(url.search)
      const isPricePa = params.has('price_pa_to') || params.has('price_pa_from')

      this.formData.lease_type = lease_type || ''

      // @ts-ignore
      this.formData.cities = params?.get('by')?.split(',') || []

      this.formData.size_from = Number(params?.get('size_from')) || initialFormData().size_from
      this.formData.size_to = Number(params?.get('size_to')) || initialFormData().size_to
      this.formData.price_from = Number(params?.get('price_from')) || initialFormData().price_from
      this.formData.price_to = Number(params?.get('price_to')) || initialFormData().price_to
      this.formData.price_pa_from =
        Number(params?.get('price_pa_from')) || initialFormData().price_pa_from
      this.formData.price_pa_to =
        Number(params?.get('price_pa_to')) || initialFormData().price_pa_to

      this.formData.price_type = isPricePa ? 'size_pa' : 'monthly'

      // Fetch citited for the filter.
      await this.fetchCitites(lease_type ?? undefined)

      // @ts-ignore
      city && this.formData.cities.unshift(city)
      await this.fetchLeases(this.formData)

      // When cities filter changes, update the url.
      // @ts-ignore
      this.$watch('formData.cities', () => {
        this.updateUrlArea(this.formData.cities)
      })
      // When slide filter changes, update the url.
      // @ts-ignore
      this.$watch('formData.size_from', () => {
        this.updateUrlSliderParams(this.formData.size_from, 'size_from')
      })
      // @ts-ignore
      this.$watch('formData.size_to', () => {
        this.updateUrlSliderParams(this.formData.size_to, 'size_to')
      })
      // @ts-ignore
      this.$watch('formData.price_from', () => {
        this.updateUrlSliderParams(this.formData.price_from, 'price_from')
      })
      // @ts-ignore
      this.$watch('formData.price_to', () => {
        this.updateUrlSliderParams(this.formData.price_to, 'price_to')
      })
      // @ts-ignore
      this.$watch('formData.price_pa_from', () => {
        this.updateUrlSliderParams(this.formData.price_pa_from, 'price_pa_from')
      })
      // @ts-ignore
      this.$watch('formData.price_pa_to', () => {
        this.updateUrlSliderParams(this.formData.price_pa_to, 'price_pa_to')
      })
      // @ts-ignore
      this.$watch('formData.price_type', () => {
        this.updateUrlPriceTypeParams(
          this.formData.price_type,
          this.formData.price_from,
          this.formData.price_to,
          this.formData.price_pa_from,
          this.formData.price_pa_to
        )
      })

      this.handleDirty()

      // @ts-ignore
      this.$watch(
        'formData',
        Alpine.debounce(() => {
          // Compare initial formData with current formData and set isDirty
          this.handleDirty()

          // Call API everytime the formData changes.
          this.fetchLeases(this.formData)

          // Virtual pageview when filter change
          let additional = {}

          if (this.formData.lease_type) {
            additional = {
              siteSection: this.formData.lease_type,
              propertyType: this.formData.lease_type,
              customerType:
                this.formData.lease_type === 'bolig'
                  ? 'privat'
                  : `erhverv_${this.formData.lease_type}`,
            }
          } else {
            additional = {
              siteSection: 'udlejning',
            }
          }
          pushDataLayer({
            event: 'virtualPageview',
            pageUrl: window.location.href,
            pagePath: window.location.pathname,
            pageTitle: 'Udlejning',
            ...additional,
          })
        }, 500)
      )

      // When lease_type filter changes, update the url.
      // @ts-ignore
      this.$watch('formData.lease_type', () => {
        if (this.formData.lease_type === 'bolig') {
          this.formData.price_type = 'monthly'
        }
        this.fetchCitites(this.formData.lease_type)
        this.updateUrlType(this.formData.lease_type, this.formData.cities)
      })
    },
  }))
})
